import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import {
  hexToRgb,
  rgbToHex,
  getNearestColorName,
  //colorDistance,
} from "../utils/colours"; // adjust path as needed

const ColourPicker = ({
  onChange,
  originalPalette,
  filteredPalette,
  setFilteredPalette,
  colours,
}) => {
  const [colourInputs, setColourInputs] = useState([
    { id: uuidv4(), hex: "#ffffff" },
  ]);

  const apiTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (apiTimeout.current) clearTimeout(apiTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (filteredPalette?.length > 0 && colours?.length > 0) {
      const synced = filteredPalette.map((_, index) => ({
        id: uuidv4(),
        hex: colours[index] ? rgbToHex(colours[index]) : "#ffffff",
      }));
      setColourInputs(synced);
    }
  }, [filteredPalette, colours]);

  const handleRemoveColor = (indexToRemove) => {
    const updated = filteredPalette.filter((_, idx) => idx !== indexToRemove);
    setFilteredPalette(updated);
    if (colourInputs.length > updated.length) {
      const updatedColourInputs = colourInputs.slice(0, updated.length);
      setColourInputs(updatedColourInputs);
      onChange(updatedColourInputs.map((item) => hexToRgb(item.hex)));
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const updated = Array.from(colourInputs);
    const [removed] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, removed);
    setColourInputs(updated);
    onChange(updated.map((item) => hexToRgb(item.hex)));
  };

  const resetToOriginal = () => {
    if (originalPalette?.length) {
      const newColourInputs = originalPalette.map((color) => ({
        id: uuidv4(),
        hex: color,
      }));
      setColourInputs(newColourInputs);
      onChange(newColourInputs.map((item) => hexToRgb(item.hex)));
      setFilteredPalette(originalPalette);
    }
  };

  const isValidHex = (hex) => /^#[0-9A-Fa-f]{6}$/.test(hex);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId="replacement"
          direction="vertical"
          isDropDisabled={false}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-4"
            >
              {filteredPalette.map((color, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,2fr)] gap-4 items-center relative bg-[#1a1a1a] border border-[#2f2f2f] rounded-xl px-4 py-3 shadow-md shadow-[#b89d5b]/10 transition-colors"
                >
                  {/* Original swatch */}
                  <div className="relative flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-full border border-[#333] shadow-sm"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                    <span
                      className="text-xs text-marbleWhite font-medium truncate"
                      title={getNearestColorName(color)}
                    >
                      {getNearestColorName(color)}
                    </span>
                    <button
                      className="absolute -top-2 -right-2 text-xs text-rose-500 font-bold"
                      onClick={() => handleRemoveColor(index)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>

                  {/* Arrow */}
                  <div className="text-center text-lg text-slate-300">→</div>

                  {/* Replacement */}
                  {colourInputs[index] && (
                    <Draggable
                      key={colourInputs[index].id}
                      draggableId={colourInputs[index].id}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="flex items-center gap-2"
                        >
                          {/* Swatch */}
                          <label
                            title={colourInputs[index].hex}
                            className="block w-7 h-7 rounded-full border border-[#333] shadow-sm cursor-pointer"
                            style={{
                              backgroundColor: colourInputs[index].hex,
                            }}
                          >
                            <input
                              type="color"
                              value={colourInputs[index].hex}
                              onChange={(e) => {
                                const newHex = e.target.value;
                                setColourInputs((prev) =>
                                  prev.map((item) =>
                                    item.id === colourInputs[index].id
                                      ? { ...item, hex: newHex }
                                      : item
                                  )
                                );

                                // Debounce parent update
                                if (apiTimeout.current)
                                  clearTimeout(apiTimeout.current);
                                const currentInputs = [...colourInputs];
                                apiTimeout.current = setTimeout(() => {
                                  onChange(
                                    currentInputs.map((item) =>
                                      item.id === colourInputs[index].id
                                        ? hexToRgb(newHex)
                                        : hexToRgb(item.hex)
                                    )
                                  );
                                }, 300);
                              }}
                              className="sr-only"
                            />
                          </label>

                          {/* Hex input */}
                          <input
                            type="text"
                            value={colourInputs[index].hex}
                            onChange={(e) => {
                              const newHex = e.target.value;
                              setColourInputs((prev) =>
                                prev.map((item) =>
                                  item.id === colourInputs[index].id
                                    ? { ...item, hex: newHex }
                                    : item
                                )
                              );

                              // Debounce parent update
                              if (apiTimeout.current)
                                clearTimeout(apiTimeout.current);
                              apiTimeout.current = setTimeout(() => {
                                if (isValidHex(newHex)) {
                                  onChange(
                                    colourInputs.map((item) =>
                                      item.id === colourInputs[index].id
                                        ? hexToRgb(newHex)
                                        : hexToRgb(item.hex)
                                    )
                                  );
                                }
                              }, 300);
                            }}
                            maxLength={7}
                            className="w-[72px] text-xs px-2 py-1 bg-slate-950 text-marbleWhite border border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-vedicGold font-mono"
                          />

                          {/* Name */}
                          <span
                            className="text-xs text-marbleWhite font-medium truncate min-w-0"
                            title={getNearestColorName(colourInputs[index].hex)}
                          >
                            {getNearestColorName(colourInputs[index].hex)}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  )}
                </div>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button
          onClick={resetToOriginal}
          className="rounded-full px-4 py-2 font-semibold text-rose-400 border border-rose-400 hover:bg-rose-500 hover:text-white transition"
        >
          Reset to Original
        </button>
      </div>
    </div>
  );
};

export default ColourPicker;
